import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import "./contador.scss";

export function CountdownBar() {
  const [timeLeft, setTimeLeft] = useState<number>(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const navigate = useNavigate();

  // guarda o total para calcular % (sem “resetar” a cada tick)
  const totalRef = useRef<number>(1);

  // 1) Busca o tempo no backend e inicia o contador
  useEffect(() => {
    let cancelled = false;

    async function loadTime() {
      try {
        setLoading(true);
        setError(null);

        const dados = JSON.parse(sessionStorage.getItem("dadosLinkAtual")!);

        const minutes = Number(dados?.outTime ?? 0);
        const seconds = Math.max(0, Math.floor(minutes * 60));

        if (cancelled) return;

        totalRef.current = Math.max(1, seconds);
        setTimeLeft(seconds);
      } catch (e: any) {
        if (cancelled) return;
        setError(e?.message ?? "Erro ao buscar tempo");
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    loadTime();

    return () => {
      cancelled = true;
    };
  }, []);

  // 2) Timer do countdown (1 vez só)
  useEffect(() => {
    if (loading) return;
    if (error) return;
    if (timeLeft <= 0) return;

    const timer = setInterval(() => {
      setTimeLeft((prev) => Math.max(0, prev - 1));
    }, 1000);

    return () => clearInterval(timer);
  }, [timeLeft, loading, error]);

  // 3) Navega quando zerar
  useEffect(() => {
    if (!loading && !error && timeLeft === 0) {
      navigate("/status");
    }
  }, [timeLeft, loading, error, navigate]);

  const formatTime = (s: number) => {
    const total = Math.max(0, Math.floor(s));

    const days = Math.floor(total / 86400);
    const hours = Math.floor((total % 86400) / 3600);
    const minutes = Math.floor((total % 3600) / 60);
    const seconds = total % 60;

    const mm = minutes.toString().padStart(2, "0");
    const ss = seconds.toString().padStart(2, "0");

    // dias + horas + minutos + segundos
    if (days > 0) return `${days}d ${hours}h ${mm}m ${ss}s`;

    // horas + minutos + segundos
    if (hours > 0) return `${hours}h ${mm}m ${ss}s`;

    // minutos + segundos (comportamento “antigo”, só melhor formatado)
    return `${minutes}:${ss}`;
  };

  const { progress, color } = useMemo(() => {
    const total = Math.max(1, totalRef.current);
    const p = 1 - Math.max(0, Math.min(1, timeLeft / total)); // 0..1

    let c = "#0F62FE"; // azul
    if (p >= 0.5) c = "#F2C94C"; // amarelo
    if (p >= 0.75) c = "#EB5757"; // vermelho

    return { progress: p, color: c };
  }, [timeLeft]);

  if (loading) {
    return (
      <div className="countdown-bar">
        <div className="box-contador">
          <span className="label">Carregando tempo...</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="countdown-bar">
        <div className="box-contador">
          <span className="label">
            Falha ao carregar tempo: <strong>{error}</strong>
          </span>
        </div>
      </div>
    );
  }

  return (
    <div className="countdown-bar">
      <div className="box-contador">
        <span
          className="status-dot"
          style={
            {
              "--fill": `${(progress * 100).toFixed(2)}%`,
              "--fillColor": color,
            } as React.CSSProperties
          }
          aria-hidden="true"
        />
        <span className="label">
          Tempo restante: <strong>{formatTime(timeLeft)}</strong>
        </span>
      </div>
    </div>
  );
}